<template>
  <div class="chat-icon-container">
    <span :class="`svg-icon svg-icon-xl svg-icon-${currentColor}`" style="cursor: pointer;">
      <!--begin::Svg Icon | path:/metronic/theme/html/demo1/dist/assets/media/svg/icons/Communication/Group-chat.svg-->
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="24px" height="24px"
        viewBox="0 0 24 24" version="1.1">
        <title>{{ props.svgTitle }}</title>
        <desc>Created with Sketch.</desc>
        <defs></defs>
        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
          <rect x="0" y="0" width="24" height="24"></rect>
          <path
            d="M16,15.6315789 L16,12 C16,10.3431458 14.6568542,9 13,9 L6.16183229,9 L6.16183229,5.52631579 C6.16183229,4.13107011 7.29290239,3 8.68814808,3 L20.4776218,3 C21.8728674,3 23.0039375,4.13107011 23.0039375,5.52631579 L23.0039375,13.1052632 L23.0206157,17.786793 C23.0215995,18.0629336 22.7985408,18.2875874 22.5224001,18.2885711 C22.3891754,18.2890457 22.2612702,18.2363324 22.1670655,18.1421277 L19.6565168,15.6315789 L16,15.6315789 Z"
            fill="#000000"></path>
          <path
            d="M1.98505595,18 L1.98505595,13 C1.98505595,11.8954305 2.88048645,11 3.98505595,11 L11.9850559,11 C13.0896254,11 13.9850559,11.8954305 13.9850559,13 L13.9850559,18 C13.9850559,19.1045695 13.0896254,20 11.9850559,20 L4.10078614,20 L2.85693427,21.1905292 C2.65744295,21.3814685 2.34093638,21.3745358 2.14999706,21.1750444 C2.06092565,21.0819836 2.01120804,20.958136 2.01120804,20.8293182 L2.01120804,18.32426 C1.99400175,18.2187196 1.98505595,18.1104045 1.98505595,18 Z M6.5,14 C6.22385763,14 6,14.2238576 6,14.5 C6,14.7761424 6.22385763,15 6.5,15 L11.5,15 C11.7761424,15 12,14.7761424 12,14.5 C12,14.2238576 11.7761424,14 11.5,14 L6.5,14 Z M9.5,16 C9.22385763,16 9,16.2238576 9,16.5 C9,16.7761424 9.22385763,17 9.5,17 L11.5,17 C11.7761424,17 12,16.7761424 12,16.5 C12,16.2238576 11.7761424,16 11.5,16 L9.5,16 Z"
            fill="#000000" opacity="0.3"></path>
        </g>
      </svg>
      <!--end::Svg Icon-->
    </span>

    <!-- Unread Messages Badge -->
    <div v-if="unreadCount > 0" class="unread-badge" :class="{ 'pulsing': unreadCount > 0 }"
      style="background: var(--bs-primary, #3699FF);">
      {{ unreadCount > 99 ? '99+' : unreadCount }}
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { getUnreadMessagesCount } from '@/js/utils/firebase';
import { initializeChatNotificationSound, playChatNotificationSound } from '@/js/utils/chat-notification';

const props = defineProps({
  svgColor: {
    type: String,
    default: 'primary',
  },
  svgTitle: {
    type: String,
    default: 'Stockholm-icons / Communication / Group-chat',
  },
  unreadCount: {
    type: Number,
    default: 0,
  }
});

const store = useStore();
const unreadCount = ref(0);
let previousUnreadCount = 0;
let hasLoadedInitialCount = false;
let stopUnreadListener = () => {};
let stopSoundInitializer = () => {};

// Determine the icon color based on unread messages
const currentColor = computed(() => {
  return unreadCount.value > 0 ? 'primary' : props.svgColor;
});

// Set up real-time listener for unread messages
onMounted(() => {
  stopSoundInitializer = initializeChatNotificationSound();

  const type = store.getters.role.split('-')[0];
  const id = store.getters.user.user.role?.id;
  const mappedType = type === 'staff' ? 'staffs' : type === 'customer' ? 'customers' : type;
  const userKey = `${mappedType}-${id}`;
  const brandId = store.getters.brand?.id;

  // Listen for unread messages count changes
  stopUnreadListener = getUnreadMessagesCount(brandId, userKey, (count) => {
    if (hasLoadedInitialCount && count > previousUnreadCount) {
      void playChatNotificationSound();
    }

    previousUnreadCount = count;
    hasLoadedInitialCount = true;
    unreadCount.value = count;
  });
});

onUnmounted(() => {
  stopUnreadListener();
  stopSoundInitializer();
});
</script>

<style scoped>
.chat-icon-container {
  position: relative;
  display: inline-block;
}

.unread-badge {
  position: absolute;
  top: -2px;
  right: -6px;
  /* background: 'primary'; */
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: bold;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.unread-badge.pulsing {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
  }
}

/* Ensure the SVG icon changes color smoothly */
.svg-icon {
  transition: all 0.3s ease;
}
</style>
